import {
  DropdownButtonType,
  List,
  ListItem,
  SingleSelectDropdown
  } from '@tableau/widgets';
import { Dictionary } from 'underscore';

import { Messages } from '../../codegen/Localize';
import { getExplorableItemDefinition } from '../../spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { SiteRoleOption, SiteRoleOptionsResource } from '../services/resources/SiteRoleOptions';
import { SiteUsers } from '../services/resources/SiteUsers';
import React = require('react');

export interface SiteRolePickerDropdownWithCountProps {
  includeAnySiteRoleOption: boolean; // if True, dropdown includes "Any site role"
  testId: string;
  setFilterValue: (siteRole: string) => void;
  getSiteRoleCountsFunction?: () => Promise<Dictionary<number>>;
}

function listItemFromSiteRole(siteRoleLocString: string, count: number): ListItem {
  const content = <>
    <span style={{float: 'left' }}>{siteRoleLocString}</span>
    <span style={{float: 'right'}}>{count}</span>
  </>;
  return {
    value: siteRoleLocString,
    content
  };
}

function convertSiteRoleOptionToListItemFunction(siteRoleCounts: Dictionary<number>) {
  return (siteRoleOption: SiteRoleOption) => {
    const siteRoleLocString = siteRoleOption.name();
    return listItemFromSiteRole(siteRoleLocString, siteRoleCounts[siteRoleOption.role]);
  };

}

async function getSiteRoleCounts(): Promise<Dictionary<number>> {
  const userCountsByRole = SiteUsers.instance.getUserCountsBySiteRole(getExplorableItemDefinition('siteuser').getBasicQuery());
  return userCountsByRole;
}

export function SiteRolePickerDropdownWithCount(props: SiteRolePickerDropdownWithCountProps): JSX.Element {
  const [siteRoleCounts, setSiteRoleCounts] = React.useState({} as Dictionary<number>); // Set in useEffect
  const [selectedSiteRole, setSelectedSiteRole] = React.useState(SiteRoleOptionsResource.instance.getAnySiteRole().name()); // Set in dropdown.onChange

  React.useEffect(
    () => {
      let isMounted = true; // very cool promise cleanup, see https://juliangaramendy.dev/use-promise-subscription/
      const getSiteRoleCountsFunction = props.getSiteRoleCountsFunction ?? getSiteRoleCounts;
      getSiteRoleCountsFunction().then((counts) => {
        if (isMounted) {
          setSiteRoleCounts(counts);
        }
      });
      return () => isMounted = false;
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  let siteRoles: SiteRoleOption[] = [];
  if (props.includeAnySiteRoleOption) {
    const anySiteRoleOption = SiteRoleOptionsResource.instance.getAnySiteRole();
    anySiteRoleOption && siteRoles.push(anySiteRoleOption);
  }
  const allSiteRoleOptions = SiteRoleOptionsResource.instance.getAllSiteRoleOptions();
  allSiteRoleOptions && siteRoles.push(...allSiteRoleOptions);

  const dropdownListItems: ListItem[] = siteRoles.map(convertSiteRoleOptionToListItemFunction(siteRoleCounts));
  const dropdownList = <List items={dropdownListItems} />;

  const singleSelectDropdownProps = {
    type: DropdownButtonType.Outline,
    label: Messages.siteRole(),
    list: dropdownList,
    value: selectedSiteRole,
    onChange: (value: string) => {
      setSelectedSiteRole(value);
      const selectedRoleIndex = siteRoles.findIndex((siteRoleOption: SiteRoleOption) => {
        return siteRoleOption.name() === value;
      });
      props.setFilterValue(siteRoles[selectedRoleIndex].role);
    }
  };

  return <SingleSelectDropdown {...singleSelectDropdownProps}>
    {selectedSiteRole}
  </SingleSelectDropdown>;
}